import React from 'react';
import { Video, Monitor, Calendar, Clock, Target, ArrowRight, BadgeCheck, Users, ShieldCheck } from 'lucide-react';
import { heroData } from '../data/mock';

const Hero = () => {
  const iconMap = {
    video: Video,
    monitor: Monitor,
    calendar: Calendar,
    clock: Clock,
    target: Target
  };

  const scrollToForm = () => {
    document.querySelector('#inscricao')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center pt-24 pb-16 overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img 
          src="https://customer-assets.emergentagent.com/job_4f5cf654-3a0c-4d01-a704-3b564425efff/artifacts/cseqiv82_FACE_WEB_Assets_Banner-Video-Poster_2024-09-23_Rev.0.jpeg"
          alt="WING Console"
          className="w-full h-full object-cover object-center"
        />
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-5xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-yellow-500/10 border border-yellow-500/30 px-4 py-2 rounded-full mb-6 md:mb-8 animate-fadeInUp">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
            <span className="text-yellow-400 font-semibold text-xs md:text-sm">{heroData.limitedSpots}</span>
          </div>

          {/* Main Title */}
          <h1 className="text-[2rem] leading-[1.1] sm:text-4xl md:text-5xl lg:text-7xl font-bold mb-4 md:mb-6 animate-fadeInUp stagger-1">
            <span className="text-white">Domine a Console </span>
            <span className="highlight-marker-solid" data-testid="hero-highlight">WING</span>
            <br />
            <span className="text-white">com quem é </span>
            <span className="gradient-text">referência</span>
          </h1>

          {/* Description */}
          <p className="text-sm sm:text-base md:text-xl lg:text-2xl text-gray-300 mb-6 md:mb-8 px-2 sm:px-4 leading-relaxed animate-fadeInUp stagger-2 max-w-3xl mx-auto">
            {heroData.description}
          </p>

          {/* Features Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 sm:gap-3 md:gap-4 mb-7 md:mb-10 max-w-4xl mx-auto px-2">
            {heroData.features.map((feature, index) => {
              const Icon = iconMap[feature.icon];
              return (
                <div
                  key={index}
                  className="glass p-2.5 md:p-4 rounded-lg flex flex-col items-center gap-1.5 md:gap-2 hover:bg-white/10 transition-all duration-300 card-hover animate-fadeInUp"
                  style={{ animationDelay: `${0.3 + index * 0.1}s` }}
                >
                  <Icon className="w-5 h-5 md:w-8 md:h-8 text-yellow-400" />
                  <span className="text-white text-[11px] md:text-sm font-medium text-center leading-tight">{feature.text}</span>
                </div>
              );
            })}
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center items-center animate-fadeInUp stagger-3 px-4">
            <button
              onClick={scrollToForm}
              className="btn-primary bg-yellow-500 hover:bg-yellow-600 text-black px-6 md:px-8 py-3 md:py-4 rounded-lg font-bold text-base md:text-lg flex items-center gap-2 glow-yellow w-full sm:w-auto justify-center"
            >
              {heroData.ctaText}
              <ArrowRight className="w-4 h-4 md:w-5 md:h-5" />
            </button>
            <a
              href="#sobre"
              className="btn-secondary border-2 border-white text-white px-6 md:px-8 py-3 md:py-4 rounded-lg font-bold text-base md:text-lg hover:bg-white hover:text-black transition-all duration-300 w-full sm:w-auto text-center"
            >
              SAIBA MAIS
            </a>
          </div>

          {/* Trust Badge - Otimizado para Mobile */}
          <div className="mt-8 md:mt-12 grid grid-cols-3 gap-2 sm:gap-4 max-w-md sm:max-w-2xl mx-auto animate-fadeInUp stagger-4 px-2">
            <div className="flex flex-col items-center gap-1.5 sm:gap-2">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-yellow-500/15 border border-yellow-500/30 rounded-full flex items-center justify-center">
                <BadgeCheck className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-400" />
              </div>
              <span className="text-[11px] sm:text-sm text-gray-300 text-center leading-tight font-medium">Instrutor<br />Oficial</span>
            </div>
            <div className="flex flex-col items-center gap-1.5 sm:gap-2 border-x border-white/10">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-yellow-500/15 border border-yellow-500/30 rounded-full flex items-center justify-center">
                <Users className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-400" />
              </div>
              <span className="text-[11px] sm:text-sm text-gray-300 text-center leading-tight font-medium">4 Aulas ao Vivo<br />+ 20 Gravadas</span>
            </div>
            <div className="flex flex-col items-center gap-1.5 sm:gap-2">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-500/15 border border-green-500/30 rounded-full flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 sm:w-6 sm:h-6 text-green-400" />
              </div>
              <span className="text-[11px] sm:text-sm text-gray-300 text-center leading-tight font-medium">Garantia<br />100%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Gradient */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black to-transparent"></div>
    </section>
  );
};

export default Hero;
